// ============================================================
// HandoutsContext (v8.363.10) - what the GM shows, for the drawer, the
// header toggle, the lightbox and the vox line that carries a thumbnail.
// Composed in DeathWatchSimulator from useHandouts + useCurtain.
// ============================================================
import { createContext, useContext, type ReactNode } from 'react';
import type { BatchItem, BatchResult, Handout, RevealTarget, Usage } from '../engine/handouts';
import type { ChatChannel, Person } from '../engine/chat';
import type { HandoutFormValues } from '../components/HandoutForm';
import type { Library } from '../assetStore';
import type { AssetRow } from '../engine/assets/types';

export interface HandoutsContextValue {
  handouts: Handout[];
  usage: Usage;
  busy: string | null;
  isGm: boolean;
  people: Person[];
  channels: ChatChannel[];
  open: boolean;
  toggle: () => void;
  close: () => void;
  /** Posts every file that fits, in order; resolves with the ones it did not take. */
  postFiles: (items: readonly BatchItem[]) => Promise<BatchResult>;
  postNote: (v: HandoutFormValues) => Promise<string | null>;
  postLink: (v: HandoutFormValues) => Promise<string | null>;
  update: (id: string, patch: Partial<HandoutFormValues>) => Promise<void>;
  reveal: (
    id: string,
    target: RevealTarget,
    channels: readonly ChatChannel[],
    opts?: { announce?: boolean; present?: boolean },
  ) => Promise<void>;
  /** Put a shown handout up on every screen in its audience (v9.8.0); `endPresent` takes it down for everyone. */
  present: (id: string) => Promise<void>;
  endPresent: (id: string) => Promise<void>;
  /** The handout that is up in this viewer's feed, if any. */
  presenting: string | null;
  /** Shown to me and not opened yet (v9.8.0, engine/handouts/seen); always empty for the GM. */
  unread: ReadonlySet<string>;
  /** The GM's: how many of those it was shown to have opened it. */
  seenOf: (h: Handout) => { seen: number; of: number };
  remove: (id: string) => Promise<void>;
  removeMany: (ids: readonly string[]) => Promise<void>;
  fileUrl: (h: Handout, field: 'file' | 'thumb') => Promise<string>;
  /** The lightbox: which handout is open large, if any. */
  viewing: string | null;
  view: (id: string) => void;
  closeView: () => void;
  /** The GM's asset library, for "From my library…" (v11.82.0); null without one. */
  library?: HandoutLibrary | null;
}

export interface HandoutLibrary {
  lib: Library;
  /** The library's owner, for the picker's pictures. */
  uid: string;
  /** May this server hold files? The picker explains when it cannot. */
  filesAvailable: boolean;
  /** Post an image or handout asset as a new handout; rejects with the sentence to show. */
  pick: (row: AssetRow) => Promise<void>;
}

const HandoutsContext = createContext<HandoutsContextValue | null>(null);

export function HandoutsProvider({ value, children }: { value: HandoutsContextValue | null; children: ReactNode }) {
  return <HandoutsContext.Provider value={value}>{children}</HandoutsContext.Provider>;
}

/** Null outside a session or before the provider mounts. */
export function useHandoutsContext(): HandoutsContextValue | null {
  return useContext(HandoutsContext);
}
